import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useAccountApi } from '../api/AccountApiProvider';
import { toApiError } from '../api/errors';
import type { AccountSession, ChangePasswordInput, SignInInput } from '../api/types';

/**
 * The account session: a provider account, on the provider's own domain.
 *
 * It is a different type from the cluster session and lives behind a different
 * hook, so a screen holding one cannot be handed the other. Nothing here opens
 * a tenancy's hosts — what opens those is the tenancy's identity provider, on
 * the tenancy's own domain.
 *
 * There is no token in this file and nowhere for one to be kept: the session is
 * a cookie the browser holds and script cannot read. Signing in changes nothing
 * here except what the server then says about who we are.
 */

type Status = 'loading' | 'ready';

export interface AccountSessionState {
  status: Status;
  /** `null` means signed out. */
  session: AccountSession | null;
  signIn: (input: SignInInput) => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * Changes the caller's own password. Every session of the account closes with
   * it, this one included, so the browser is signed out on success.
   */
  changePassword: (input: ChangePasswordInput) => Promise<void>;
}

const AccountSessionContext = createContext<AccountSessionState | null>(null);

export function AccountSessionProvider({ children }: { children: ReactNode }) {
  const api = useAccountApi();
  const [status, setStatus] = useState<Status>('loading');
  const [session, setSession] = useState<AccountSession | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api.getSession(controller.signal).then(
      (value) => {
        if (controller.signal.aborted) return;
        setSession(value);
        setStatus('ready');
      },
      () => {
        // No session and no server both mean the same thing here: show the
        // sign-in screen rather than a spinner nobody can get past.
        if (controller.signal.aborted) return;
        setSession(null);
        setStatus('ready');
      },
    );
    return () => {
      controller.abort();
    };
  }, [api]);

  const signIn = useCallback(
    async (input: SignInInput) => {
      try {
        setSession(await api.signIn(input));
      } catch (error: unknown) {
        throw toApiError(error);
      }
    },
    [api],
  );

  const signOut = useCallback(async () => {
    try {
      await api.signOut();
    } finally {
      setSession(null);
    }
  }, [api]);

  const changePassword = useCallback(
    async (input: ChangePasswordInput) => {
      try {
        await api.changePassword(input);
      } catch (error: unknown) {
        throw toApiError(error);
      }
      // The server closed every session of this account, so staying on screen
      // as though signed in would be the console telling a comfortable lie.
      setSession(null);
    },
    [api],
  );

  const value = useMemo<AccountSessionState>(
    () => ({ status, session, signIn, signOut, changePassword }),
    [status, session, signIn, signOut, changePassword],
  );

  return <AccountSessionContext value={value}>{children}</AccountSessionContext>;
}

export function useAccountSession(): AccountSessionState {
  const value = useContext(AccountSessionContext);
  if (value === null) {
    throw new Error('useAccountSession() used outside <AccountSessionProvider>');
  }
  return value;
}
